import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/lib/entries";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import ReviewTag from "@/components/ReviewTag";
import { Star, Search, X, Send, CheckCircle2, XCircle, History } from "lucide-react";

const TABS = [
  { key: "ongoing",          label: "Ongoing",          dot: "bg-orange-500" },
  { key: "reviewed",         label: "Reviewed",         dot: "bg-emerald-500" },
  { key: "not_reviewed",     label: "Not Reviewed",     dot: "bg-red-500" },
  { key: "not_asked",        label: "Did Not Ask",      dot: "bg-sky-500" },
  { key: "already_reviewed", label: "Already Reviewed", dot: "bg-violet-500" },
];

const EMPTY_COPY = {
  ongoing:          "No open review conversations. Send a Google review link from the Completed Treatment sheet to start one.",
  reviewed:         "No reviews received yet.",
  not_reviewed:     "No reviews closed as not reviewed.",
  not_asked:        "No entries marked as \"Did not ask for review\" yet.",
  already_reviewed: "No entries marked as \"Already Reviewed\" yet.",
};

export default function ReviewsSheet() {
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("ongoing");
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);

  // Dialog state
  const [remindOpen, setRemindOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [activeRow, setActiveRow] = useState(null);
  const [reminderNote, setReminderNote] = useState("");
  const [closeOutcome, setCloseOutcome] = useState(null);
  const [closeNote, setCloseNote] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get("/sheets/reviews"); setRows(data); }
    catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = { ongoing: 0, reviewed: 0, not_reviewed: 0, not_asked: 0, already_reviewed: 0 };
    for (const r of rows) if (c[r.review_state] !== undefined) c[r.review_state] += 1;
    return c;
  }, [rows]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows.filter((e) => {
      if (e.review_state !== tab) return false;
      if (!s) return true;
      return e.patient_name.toLowerCase().includes(s);
    });
  }, [rows, q, tab]);

  const openRemind = (row) => { setActiveRow(row); setReminderNote(""); setRemindOpen(true); };
  const openClose = (row, outcome) => {
    setActiveRow(row); setCloseOutcome(outcome); setCloseNote(""); setCloseOpen(true);
  };
  const openHistory = (row) => { setActiveRow(row); setHistoryOpen(true); };

  const sendReminder = async () => {
    if (!activeRow) return;
    setBusyId(activeRow.id);
    try {
      const { data } = await api.post(`/entries/${activeRow.id}/review/remind`, {
        note: reminderNote.trim(),
      });
      setRows((prev) => prev.map((r) => (r.id === data.id ? data : r)));
      toast.success("Reminder logged");
      setRemindOpen(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const closeReview = async () => {
    if (!activeRow || !closeOutcome) return;
    setBusyId(activeRow.id);
    try {
      const { data } = await api.post(`/entries/${activeRow.id}/review/close`, {
        outcome: closeOutcome,
        note: closeNote.trim(),
      });
      setRows((prev) => prev.map((r) => (r.id === data.id ? data : r)));
      toast.success(closeOutcome === "reviewed" ? "Marked as Reviewed" : "Marked as Not Reviewed");
      setCloseOpen(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const reminderCount = (row) => (row.review_reminders || []).filter((r) => r.kind === "reminder").length;
  const lastAt = (row) => {
    const list = row.review_reminders || [];
    if (list.length === 0) return null;
    return list[list.length - 1].at;
  };

  return (
    <div className="px-6 lg:px-10 pt-4 lg:pt-6 pb-3 max-w-[1400px] mx-auto">
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 rounded-lg bg-orange-50 text-orange-700 flex items-center justify-center">
          <Star size={18} />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wide text-slate-400 leading-none">Google review conversations</p>
          <h1 className="font-display text-2xl font-bold text-slate-900 leading-tight">Reviews</h1>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-3">
        <TabsList className="bg-slate-100" data-testid="reviews-tabs">
          {TABS.map((t) => (
            <TabsTrigger
              key={t.key}
              value={t.key}
              data-testid={`reviews-tab-${t.key}`}
              className="data-[state=active]:bg-white data-[state=active]:shadow-sm"
            >
              <span className={`h-1.5 w-1.5 rounded-full mr-2 ${t.dot}`} />
              {t.label}
              <span className="ml-2 text-[11px] font-medium text-slate-500" data-testid={`reviews-tab-count-${t.key}`}>
                ({counts[t.key]})
              </span>
            </TabsTrigger>
          ))}
        </TabsList>

        {TABS.map((t) => (
          <TabsContent key={t.key} value={t.key} className="mt-3 space-y-2">
            <Card className="border-slate-200 p-3">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input className="pl-9 pr-9" placeholder="Search patient name in this tab..."
                       value={q} onChange={(e) => setQ(e.target.value)} data-testid="reviews-search-input" />
                {q && (
                  <button type="button" onClick={() => setQ("")} data-testid="reviews-search-clear"
                          className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center">
                    <X size={14} />
                  </button>
                )}
              </div>
            </Card>

            <Card className="mb-3 border-slate-200 overflow-hidden [&>div]:max-h-[calc(100vh-230px)] [&>div]:overflow-auto" data-testid={`reviews-sheet-${t.key}`}>
              {filtered.length === 0 ? (
                <div className="p-10 text-center text-sm text-slate-500" data-testid={`reviews-empty-${t.key}`}>
                  {q ? `No matches for "${q}" in ${t.label}.` : EMPTY_COPY[t.key]}
                </div>
              ) : (
                <Table>
                  <TableHeader className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_rgba(226,232,240,1)]">
                    <TableRow>
                      <TableHead className="w-14">S.No.</TableHead>
                      <TableHead>Patient</TableHead>
                      <TableHead>Reminders sent</TableHead>
                      <TableHead>Last action</TableHead>
                      <TableHead>Treatment</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((e, i) => {
                      const isOngoing = e.review_state === "ongoing";
                      return (
                        <TableRow key={e.id} data-testid={`review-row-${e.id}`}>
                          <TableCell className="text-slate-500 text-sm w-14" data-testid={`review-sno-${e.id}`}>{i + 1}</TableCell>
                          <TableCell className="font-medium text-slate-900">
                            <div className="flex items-center gap-2 flex-wrap">
                              <button type="button"
                                      onClick={() => openHistory(e)}
                                      data-testid={`review-name-${e.id}`}
                                      className="hover:text-orange-700 hover:underline text-left">
                                {e.patient_name}
                              </button>
                              <ReviewTag state={e.review_state} testid={`review-tag-${e.id}`} />
                            </div>
                          </TableCell>
                          <TableCell className="text-slate-700" data-testid={`review-reminder-count-${e.id}`}>
                            {reminderCount(e)}
                          </TableCell>
                          <TableCell className="text-slate-600 text-sm">
                            {isOngoing
                              ? (lastAt(e) ? fmtDateTime(lastAt(e)) : "—")
                              : (e.review_closed_at ? fmtDateTime(e.review_closed_at) : "—")}
                          </TableCell>
                          <TableCell className="text-slate-500">{fmtDate(e.entry_date)}</TableCell>
                          <TableCell className="text-right">
                            <div className="inline-grid grid-cols-2 gap-2 w-[380px]">
                              <Button
                                size="sm" variant="outline"
                                onClick={() => openHistory(e)}
                                data-testid={`review-history-btn-${e.id}`}
                                className="w-full justify-center"
                              >
                                <History size={14} className="mr-1" /> History
                              </Button>
                              {isOngoing ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openRemind(e)}
                                  disabled={busyId === e.id}
                                  className="w-full justify-center bg-white border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
                                  data-testid={`review-remind-btn-${e.id}`}
                                >
                                  <Send size={14} className="mr-1" /> Send Reminder
                                </Button>
                              ) : <span />}
                              {isOngoing ? (
                                <Button
                                  size="sm"
                                  onClick={() => openClose(e, "reviewed")}
                                  disabled={busyId === e.id}
                                  className="w-full justify-center bg-emerald-600 hover:bg-emerald-700 text-white"
                                  data-testid={`review-close-received-${e.id}`}
                                >
                                  <CheckCircle2 size={14} className="mr-1" /> Review Received
                                </Button>
                              ) : <span />}
                              {isOngoing ? (
                                <Button
                                  size="sm"
                                  onClick={() => openClose(e, "not_reviewed")}
                                  disabled={busyId === e.id}
                                  className="w-full justify-center bg-red-600 hover:bg-red-700 text-white"
                                  data-testid={`review-close-none-${e.id}`}
                                >
                                  <XCircle size={14} className="mr-1" /> No Further Follow-Up
                                </Button>
                              ) : <span />}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* Send reminder dialog */}
      <Dialog open={remindOpen} onOpenChange={setRemindOpen}>
        <DialogContent data-testid="review-remind-dialog">
          <DialogHeader>
            <DialogTitle>Send review reminder</DialogTitle>
            <DialogDescription>
              Log a reminder for <span className="font-medium text-slate-900">{activeRow?.patient_name}</span>.
              The reminder is recorded with the current date and time.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm text-slate-600">Note (optional)</label>
            <Textarea
              value={reminderNote}
              onChange={(e) => setReminderNote(e.target.value)}
              placeholder="e.g. Sent WhatsApp follow-up with the review link again"
              data-testid="review-remind-note"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemindOpen(false)}>Cancel</Button>
            <Button
              onClick={sendReminder}
              disabled={busyId === activeRow?.id}
              className="bg-sky-500 hover:bg-sky-600 text-white"
              data-testid="review-remind-confirm"
            >
              Log reminder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close review dialog */}
      <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
        <DialogContent data-testid="review-close-dialog">
          <DialogHeader>
            <DialogTitle>
              {closeOutcome === "reviewed" ? "Mark review as received" : "Close as no further follow-up"}
            </DialogTitle>
            <DialogDescription>
              {closeOutcome === "reviewed"
                ? "This patient will show as Reviewed (green) in both Reviews and Completed Treatment."
                : "This patient will show as Not Reviewed (red) in both Reviews and Completed Treatment."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm text-slate-600">Note (optional)</label>
            <Textarea
              value={closeNote}
              onChange={(e) => setCloseNote(e.target.value)}
              placeholder="Any context worth keeping on record"
              data-testid="review-close-note"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseOpen(false)}>Cancel</Button>
            <Button
              onClick={closeReview}
              disabled={busyId === activeRow?.id}
              className={
                closeOutcome === "reviewed"
                  ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                  : "bg-red-600 hover:bg-red-700 text-white"
              }
              data-testid="review-close-confirm"
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reminder history dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent data-testid="review-history-dialog">
          <DialogHeader>
            <DialogTitle>Review timeline — {activeRow?.patient_name}</DialogTitle>
            <DialogDescription>Chronological log of the Google review conversation.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[420px] overflow-y-auto space-y-2 pr-1">
            {(activeRow?.review_reminders || []).length === 0 ? (
              <div className="text-sm text-slate-500 p-6 text-center border border-dashed rounded-md">
                No entries yet.
              </div>
            ) : (
              (activeRow?.review_reminders || []).map((r) => (
                <div key={r.id} className="border border-slate-200 rounded-md p-3">
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span className="font-medium text-slate-700">
                      {r.kind === "sent" ? "Google Review Link Sent" : "Reminder"}
                    </span>
                    <span>{fmtDateTime(r.at)}</span>
                  </div>
                  {r.note ? <div className="mt-1 text-sm text-slate-700">{r.note}</div> : null}
                  {r.by ? <div className="mt-1 text-[11px] text-slate-400">by {r.by}</div> : null}
                </div>
              ))
            )}
            {activeRow?.review_state && activeRow.review_state !== "ongoing" && (
              <div className="border border-slate-200 rounded-md p-3 bg-slate-50">
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span className="font-medium text-slate-700">
                    {activeRow.review_state === "reviewed"
                      ? "Closed as Reviewed"
                      : activeRow.review_state === "not_reviewed"
                      ? "Closed as Not Reviewed"
                      : "Marked: Did not ask for review"}
                  </span>
                  <span>{activeRow.review_closed_at ? fmtDateTime(activeRow.review_closed_at) : ""}</span>
                </div>
                {activeRow.review_close_note ? (
                  <div className="mt-1 text-sm text-slate-700">{activeRow.review_close_note}</div>
                ) : null}
                {activeRow.review_closed_by ? (
                  <div className="mt-1 text-[11px] text-slate-400">by {activeRow.review_closed_by}</div>
                ) : null}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
